import React, { useEffect } from 'react';
import { graphql } from 'gatsby';

import { ContentfulLayout } from '../../../shared/constants/Contentful.constants';
import { Tag } from '../../../types/shared';

import { articleTrackPageView } from '../../../utils/deq';
import { useHomepageStaticData } from '../../../utils/generic/homepageStaticQuery';
import SEO from '../../utils/SEO';

import BaseLayout from '../../layouts/BaseLayout';
import ArticleLayout from '../../layouts/deprecated/ArticleLayout';

import { ArticlePageProps } from './ArticlePage.types';

const ArticlePage = ({ data: pageData, pageContext }: ArticlePageProps) => {
  const data = pageData.contentfulPageArticle;
  const { intakeCard } = useHomepageStaticData();
  const parseArticleTags = () => {
    if (data) {
      const tagsArray: Tag[] = [];

      data.tags?.forEach((tag) => {
        tagsArray.push({ ...(tag as Tag) });
      });

      // Make sure only unique tagnames
      const tags: Tag[] = [...new Set(tagsArray)];
      const allTagNames: string[] = [
        ...new Set(tagsArray.map((tag) => tag.name)),
      ];

      return [tags, allTagNames];
    }

    return [null, []];
  };

  const [tags, allTagNames] = parseArticleTags();

  // createdAt and updatedAt are default fields that will have a value
  // the other 2 are added later on and some articles don't have them
  const pubDate = data?.publishingTime ?? data.createdAt;
  const modDate = data?.lastContentUpdate ?? data.updatedAt;
  const readTime = `${(data?.readingMinutes ?? 0) * 60}`;
  const pageTitle = `${data?.subHeader} | ${data?.title}`.toLowerCase();

  useEffect(() => {
    articleTrackPageView(
      data.authorRef?.name,
      allTagNames as string[],
      pageTitle,
      pubDate,
      modDate,
      readTime
    );
  }, []);

  return (
    <BaseLayout layoutType={ContentfulLayout.DEFAULT}>
      <SEO
        title={data.metadataTitle}
        description={data.metadataDescription?.metadataDescription}
        lang={data.metadataLocale}
        canonical={data.metadataCanonical}
        image={data.metadataImage.gatsbyImageData?.images?.fallback?.src}
      />
      <ArticleLayout
        intakeCard={intakeCard}
        articlesData={pageContext.articles}
        data={data}
        tags={tags}
      />
    </BaseLayout>
  );
};

export const data = graphql`
  query ArticlesQuery($slug: String) {
    contentfulPageArticle(slug: { eq: $slug }) {
      slug
      title
      titleForCarousel
      subHeader
      ...ParentPageArticle
      authorRef {
        name
        url
      }
      publishingTime(formatString: "YYYY-MM-DD")
      createdAt(formatString: "YYYY-MM-DD")
      updatedAt(formatString: "YYYY-MM-DD")
      lastContentUpdate
      readingMinutes
      tags {
        id
        name
        value
        type
      }
      relatedProductTile {
        ...Card
      }
      relatedProductCard {
        ...Card
      }
      mainImage {
        gatsbyImageData(layout: FULL_WIDTH)
      }
      metadataTitle
      metadataDescription {
        metadataDescription
      }
      metadataImage {
        gatsbyImageData(layout: FULL_WIDTH)
      }
      metadataLocale
      metadataCanonical
      lead {
        id
        lead
      }
      articleType
      articleBody {
        ... on ContentfulArticleTextSection {
          id
          name
          header
          paragraphText {
            raw
            references {
              ... on ContentfulAsset {
                contentful_id
                __typename
                gatsbyImageData(layout: FULL_WIDTH, quality: 100)
              }
              ...Card
            }
          }
          __typename
        }
      }
    }
  }
`;

export default ArticlePage;
